import React, { useEffect, useRef, useState } from 'react';
import Reveal from '../components/ui/Reveal';
import { TextArea } from '../components/ui/Field';
import Tooltip from '../components/ui/Tooltip';
import { AdoptedIcon } from './AdoptedBlock';
import { DANGER_ROW_BUTTON, DISCLOSURE_INNER, ROW_BUTTON, TONAL_SMALL, disclosure, useReturnFocus } from './DocSection';
import HistoryPopover from './HistoryPopover';
import ThreadView from './ThreadView';
import { buttonClass } from '../components/ui/Button';
import type { PlanPoint } from './pointsTypes';
import type { PlanMember, PlanRole } from './types';

export interface RowPoint extends PlanPoint {
  /** Added here and not yet confirmed by the server. */
  pending?: boolean;
}

// A named group: the document section around this row is itself a `group`, and the
// shared HOVER_REVEAL would open every point's keys whenever the section is hovered.
const POINT_REVEAL =
  'opacity-0 group-hover/point:opacity-100 group-focus-within/point:opacity-100 [@media(hover:none)]:opacity-100 transition-opacity duration-150 ease-emphasized motion-reduce:transition-none';

const DANGER_TONAL = buttonClass('danger', 'sm');

const Tip: React.FC<{ tip: string; children: React.ReactElement }> = ({ tip, children }) => (
  <Tooltip label={tip}>{children}</Tooltip>
);

const THUMB_CUFF = 'M2 10h4v11H2z';
const THUMB_HAND =
  'M8 10 12.2 3.1c.4-.7 1.3-.9 2-.5.8.5 1.1 1.4.9 2.2L14.2 9H20c1.1 0 2 .9 2 2v1.2c0 .3-.1.5-.2.8l-2.7 6.6c-.3.8-1.1 1.4-1.9 1.4H10c-1.1 0-2-.9-2-2z';
const THUMB_HOLLOW = 'M10 10.6V19h7.2l2.8-6.8V11h-8.2l1.3-5.6z';

/** A thumbs-up, filled paths. Hollow until voted, so a vote reads by shape as well as tone. */
export const ThumbIcon: React.FC<{ on?: boolean; className?: string }> = ({ on = true, className = 'h-4 w-4' }) => (
  <svg aria-hidden="true" viewBox="0 0 24 24" className={`flex-shrink-0 ${className}`} fill="none">
    <path fill="currentColor" d={THUMB_CUFF} />
    <path fill="currentColor" fillRule="evenodd" d={on ? THUMB_HAND : `${THUMB_HAND}${THUMB_HOLLOW}`} />
  </svg>
);

interface PointEditorProps {
  body: string;
  onSave: (body: string) => Promise<boolean>;
  onCancel: () => void;
}

/** Enter saves, Shift+Enter starts a new line, Escape cancels. */
const PointEditor: React.FC<PointEditorProps> = ({ body, onSave, onCancel }) => {
  const [text, setText] = useState(body);
  const [busy, setBusy] = useState(false);
  const ref = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.focus();
    el.setSelectionRange(el.value.length, el.value.length);
  }, []);

  const save = async () => {
    const clean = text.trim();
    if (!clean || busy) return;
    if (clean === body.trim()) {
      onCancel();
      return;
    }
    setBusy(true);
    const ok = await onSave(clean);
    if (!ok) setBusy(false);
  };

  return (
    <div className="space-y-1.5">
      <TextArea
        ref={ref}
        value={text}
        rows={2}
        disabled={busy}
        onChange={(e) => setText(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Escape') {
            e.preventDefault();
            e.stopPropagation();
            onCancel();
          } else if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            save();
          }
        }}
        aria-label="Edit your note"
        maxLength={4000}
      />
      <div className="flex items-center gap-1">
        <button type="button" onClick={save} disabled={busy || !text.trim()} className={TONAL_SMALL}>
          Save
        </button>
        <button type="button" onClick={onCancel} disabled={busy} className={ROW_BUTTON}>
          Cancel
        </button>
        <span className="ml-2 hidden text-[11px] text-on-surface-variant sm:inline">Enter saves, Esc cancels</span>
      </div>
    </div>
  );
};

export interface PointRowProps {
  point: RowPoint;
  planId: string;
  nodeId: string;
  isOwn: boolean;
  role: PlanRole | null;
  userId: string | null;
  authorityId: string | null;
  members: PlanMember[];
  /** A non-viewer on a plan that is not archived. Locked plans keep votes open. */
  canVote: boolean;
  /** Add, edit and delete: a non-viewer on a plan that is neither locked nor archived. */
  canWrite: boolean;
  /** A manager or this topic's authority, where writes are open. */
  canAdopt: boolean;
  /** Post in the thread: a non-viewer on a plan that is not archived. */
  canDiscuss: boolean;
  onVote: (point: RowPoint) => void;
  /** Receives the row, so the panel can glide the point into the adopted block. */
  onAdopt: (point: RowPoint, row: HTMLElement | null) => Promise<void>;
  onEdit: (point: RowPoint, body: string) => Promise<boolean>;
  onDelete: (point: RowPoint) => Promise<boolean>;
  onThreadWrote: () => void;
  /** Changes when the node does; an open thread refetches. */
  refresh?: number;
}

const PointRow: React.FC<PointRowProps> = ({
  point,
  planId,
  nodeId,
  isOwn,
  role,
  userId,
  authorityId,
  members,
  canVote,
  canWrite,
  canAdopt,
  canDiscuss,
  onVote,
  onAdopt,
  onEdit,
  onDelete,
  onThreadWrote,
  refresh,
}) => {
  const [editing, setEditing] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [threadOpen, setThreadOpen] = useState(false);
  // The thread mounts on first open and stays, so closing animates and reopening is instant.
  const [threadMounted, setThreadMounted] = useState(false);
  const [vetoing, setVetoing] = useState(false);
  const [focusComposer, setFocusComposer] = useState(false);
  const [busy, setBusy] = useState(false);
  const rowRef = useRef<HTMLDivElement>(null);
  const editTrigger = useReturnFocus<HTMLButtonElement>(editing);
  const deleteTrigger = useReturnFocus<HTMLButtonElement>(confirming);
  const historyTrigger = useReturnFocus<HTMLButtonElement>(historyOpen);
  const keepRef = useRef<HTMLButtonElement>(null);

  const mayEdit = isOwn && canWrite && !point.adopted && !point.pending;
  const mayVeto = canDiscuss && !isOwn && !point.pending;
  const mayAdopt = canAdopt && !point.adopted && !point.pending;
  const edited = !!point.edited_at || point.revisions > 0;

  useEffect(() => {
    if (!mayEdit) {
      setEditing(false);
      setConfirming(false);
    }
  }, [mayEdit]);

  useEffect(() => {
    if (confirming) keepRef.current?.focus();
  }, [confirming]);

  const openThread = (withVeto: boolean) => {
    setThreadMounted(true);
    if (withVeto) {
      setVetoing(true);
      setThreadOpen(true);
      return;
    }
    const next = !threadOpen;
    setFocusComposer(next);
    setThreadOpen(next);
    if (!next) setVetoing(false);
  };

  const replyLabel = canDiscuss ? 'Reply' : 'Replies';
  const snippet = point.body.length > 60 ? `${point.body.slice(0, 60)}...` : point.body;

  return (
    <li className="group/point">
      <div ref={rowRef} className="np-state flex items-start gap-1.5 rounded-md py-1 pl-0.5 pr-1 transition-colors duration-150 ease-emphasized motion-reduce:transition-none">
        {point.pending ? (
          <span aria-hidden="true" className="w-11 flex-shrink-0" />
        ) : (
          <Tip tip={isOwn ? 'Your own note' : point.voted ? 'Take back your vote' : 'Vote for this note'}>
            <button
              type="button"
              aria-pressed={point.voted}
              aria-label={`Vote for this note, ${point.votes} ${point.votes === 1 ? 'vote' : 'votes'}`}
              disabled={isOwn || !canVote}
              onClick={() => onVote(point)}
              className={`${buttonClass('quiet', 'sm')} min-w-[44px] flex-shrink-0 tabular-nums`}
            >
              <ThumbIcon on={point.voted} />
              <span>{point.votes}</span>
            </button>
          </Tip>
        )}

        <div className="min-w-0 flex-1 pt-1">
          {editing ? (
            <PointEditor
              body={point.body}
              onCancel={() => setEditing(false)}
              onSave={async (body) => {
                const ok = await onEdit(point, body);
                if (ok) setEditing(false);
                return ok;
              }}
            />
          ) : (
            <>
              <p className="max-w-[65ch] whitespace-pre-wrap break-words text-[13px] leading-relaxed text-on-surface">
                {point.body}
                {edited && (
                  <>
                    {' '}
                    <button
                      ref={historyTrigger}
                      type="button"
                      aria-haspopup="dialog"
                      aria-expanded={historyOpen}
                      aria-label="Edited. Show earlier versions"
                      onClick={() => setHistoryOpen((o) => !o)}
                      className="rounded-sm text-[11px] text-on-surface-variant underline decoration-dotted underline-offset-2 hover:text-on-surface focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/50 [@media(pointer:coarse)]:py-1"
                    >
                      (edited)
                    </button>
                  </>
                )}
              </p>

              <Reveal show={!!(point.adopted || point.pending)}>
              {(point.adopted || point.pending) && (
                <div className="mt-0.5 flex items-center gap-1.5">
                  {point.adopted && (
                    <span className="inline-flex h-5 items-center gap-1 rounded-full bg-primary/15 pl-1.5 pr-2 text-[11px] font-semibold text-primary-ink">
                      <AdoptedIcon className="h-3 w-3" />
                      Adopted
                    </span>
                  )}
                  {point.pending && <span className="text-[11px] italic text-on-surface-variant">Saving</span>}
                </div>
              )}
              </Reveal>

              {confirming ? (
                <div
                  role="group"
                  aria-label="Delete this note?"
                  className="mt-1 flex flex-wrap items-center gap-1"
                  onKeyDown={(e) => {
                    if (e.key === 'Escape') {
                      e.preventDefault();
                      e.stopPropagation();
                      setConfirming(false);
                    }
                  }}
                >
                  <span className="mr-1 text-xs text-on-surface">Delete this note? Its discussion goes with it.</span>
                  <button
                    type="button"
                    disabled={busy}
                    onClick={async () => {
                      setBusy(true);
                      const ok = await onDelete(point);
                      if (!ok) setBusy(false);
                    }}
                    className={DANGER_TONAL}
                  >
                    Delete
                  </button>
                  <button ref={keepRef} type="button" disabled={busy} onClick={() => setConfirming(false)} className={ROW_BUTTON}>
                    Keep
                  </button>
                </div>
              ) : (
                !point.pending && (
                  <div className={`-ml-2 mt-0.5 flex flex-wrap items-center ${threadOpen ? '' : POINT_REVEAL}`}>
                    <Tip tip={canDiscuss ? 'Reply to this note, or read the replies' : 'Read the replies to this note'}>
                      <button
                        type="button"
                        aria-expanded={threadOpen}
                        onClick={() => openThread(false)}
                        aria-label={`${replyLabel}: ${snippet}`}
                        className={ROW_BUTTON}
                      >
                        {replyLabel}
                      </button>
                    </Tip>
                    {mayVeto && (
                      <Tip tip="Object to this note, with a reason. Its author and the plan's managers are told.">
                        <button type="button" onClick={() => openThread(true)} aria-label={`Veto: ${snippet}`} className={DANGER_ROW_BUTTON}>
                          Veto
                        </button>
                      </Tip>
                    )}
                    {mayAdopt && (
                      <Tip tip="Make this note part of the topic. Its wording is kept as it reads now.">
                        <button
                          type="button"
                          disabled={busy}
                          onClick={async () => {
                            setBusy(true);
                            await onAdopt(point, rowRef.current);
                            setBusy(false);
                          }}
                          aria-label={`Adopt: ${snippet}`}
                          className={ROW_BUTTON}
                        >
                          Adopt
                        </button>
                      </Tip>
                    )}
                    {mayEdit && (
                      <>
                        <Tip tip="Change the wording. Earlier versions stay in the history.">
                          <button ref={editTrigger} type="button" onClick={() => setEditing(true)} aria-label={`Edit: ${snippet}`} className={ROW_BUTTON}>
                            Edit
                          </button>
                        </Tip>
                        <Tip tip="Remove this note and its discussion">
                          <button
                            ref={deleteTrigger}
                            type="button"
                            onClick={() => setConfirming(true)}
                            aria-label={`Delete: ${snippet}`}
                            className={DANGER_ROW_BUTTON}
                          >
                            Delete
                          </button>
                        </Tip>
                      </>
                    )}
                  </div>
                )
              )}
            </>
          )}
        </div>
      </div>

      {historyOpen && (
        <HistoryPopover
          planId={planId}
          nodeId={nodeId}
          point={point}
          anchor={historyTrigger}
          onClose={() => setHistoryOpen(false)}
        />
      )}

      {threadMounted && (
        <div className={disclosure(threadOpen)} aria-hidden={!threadOpen}>
          <div className={DISCLOSURE_INNER}>
            <div className="pb-1 pl-12">
              <ThreadView
                pointId={point.id}
                pointUserId={point.user_id}
                role={role}
                userId={userId}
                authorityId={authorityId}
                canDiscuss={canDiscuss}
                members={members}
                vetoing={vetoing}
                onVetoClose={() => setVetoing(false)}
                focusComposer={focusComposer && threadOpen}
                onWrote={onThreadWrote}
                refresh={refresh}
              />
            </div>
          </div>
        </div>
      )}
    </li>
  );
};

export default PointRow;
